import { HomeyAPI, type ItemProps, type Video } from 'homey-api';

import { __DEV__ } from '../../lib/__dev__';
import { BaseApiStore, type FetchArgs } from '../BaseApiStore.ts';

export type VideoProps = ItemProps<Video>;

type VideosData = { [key: string]: Video };
type VideosById = { [key: string]: VideoProps };

interface State {
  data: null | VideosData;
  byId: null | VideosById;
  isLoading: boolean;
  error: unknown;
}

export type VideosStoreState = State;

export class VideosStore extends BaseApiStore<State, Video> {
  constructor() {
    super('videos');
  }

  override createInitialState() {
    return {
      data: null,
      byId: null,
      isLoading: true,
      error: null,
    };
  }

  override async fetch({ api, fetchPolicy, cache, abortController }: FetchArgs) {
    if (fetchPolicy === 'reset') {
      this.set(this.createInitialState());
    }

    // V2 has no videos manager; preserve the empty state on unsupported Homeys.
    if (HomeyAPI.isHomeyAPIV2(api) === true || api.videos == null) {
      this.set({
        ...this.createInitialState(),
        data: {},
        byId: {},
        isLoading: false,
      });
      return;
    }

    try {
      await this.connectManager({ manager: api.videos });

      if (abortController.signal.aborted) return;

      const videosData = await api.videos.getVideos({ $cache: cache });

      if (abortController.signal.aborted) return;

      const videos = Object.values(videosData);

      const data: VideosData = {};
      const byId: VideosById = {};

      for (const videoInstance of videos) {
        const videoReference = videoInstance.getProps();

        data[videoReference.id] = videoInstance;
        byId[videoReference.id] = videoReference;
      }

      this.set({
        ...this.createInitialState(),
        data: data,
        byId: byId,
        isLoading: false,
      });
    } catch (error) {
      if (abortController.signal.aborted) return;

      this.setFetchError({ error, fetchPolicy });
    }
  }

  override destroy(reason: string) {
    this.disconnectManager().catch(console.error);
  }

  override onAttached({ api }: { api: HomeyAPI }): void {
    if (HomeyAPI.isHomeyAPIV2(api) === true || api.videos == null) {
      return;
    }

    api.videos.on('video.create', this.handleCreate);
    api.videos.on('video.update', this.handleUpdate);
    api.videos.on('video.delete', this.handleDelete);
  }

  override onDetached({ api }: { api: HomeyAPI }): void {
    if (HomeyAPI.isHomeyAPIV2(api) === true || api.videos == null) {
      return;
    }

    api.videos.off('video.create', this.handleCreate);
    api.videos.off('video.update', this.handleUpdate);
    api.videos.off('video.delete', this.handleDelete);
  }

  getInstanceFromCache(video: { id: string }) {
    return this.getCache()[video.id];
  }

  getCache(): Readonly<Record<string, Video>> {
    const api = this.getApi();

    if (HomeyAPI.isHomeyAPIV2(api) === true || api.videos == null) {
      return {};
    }

    return api.videos.getCachedItems('video');
  }

  handleCreate = (createdVideo: Video) => {
    this.DEBUG && console.info(`create:video:${createdVideo.id}`);
    const state = this.get();
    const videoInstance = this.getInstanceFromCache(createdVideo);

    if (videoInstance == null) return;

    const videoReference = videoInstance.getProps();

    this.set({
      data: {
        ...state.data,
        [createdVideo.id]: videoInstance,
      },
      byId: {
        ...state.byId,
        [createdVideo.id]: videoReference,
      },
    });
  };

  handleUpdate = (updatedVideo: Video) => {
    this.DEBUG && console.info(`update:video:${updatedVideo.id}`);

    this.addToQueue(updatedVideo.id, updatedVideo);

    this.batch((updateQueue) => {
      const state = this.get();

      const nextById = {
        ...state.byId,
      };

      let updateCount = 0;

      for (const [id, queuedVideo] of Object.entries(updateQueue)) {
        const videoInstance = this.getInstanceFromCache(queuedVideo);

        if (state.data == null) {
          this.DEBUG && console.info(`update:video:missing-data`);
          continue;
        }

        if (videoInstance == null || state.data[id] == null) {
          this.DEBUG && console.info(`update:video:unknown:${id}`);
          continue;
        }

        const videoReference = videoInstance.getProps();

        nextById[id] = videoReference;

        updateCount++;
      }

      this.DEBUG && console.info(`update:video:batch:count:${updateCount}`);

      updateCount > 0 &&
        this.set({
          byId: nextById,
        });
    });
  };

  handleDelete = (deletedVideo: Pick<Video, 'id'>) => {
    this.DEBUG && console.info(`delete:video:${deletedVideo.id}`);
    const state = this.get();

    if (state.data == null) {
      __DEV__ && console.log(`delete:video:missing-data`);
      return;
    }

    const videoInstance = state.data[deletedVideo.id];

    if (videoInstance == null) return;

    const nextData = { ...state.data };
    const nextById = { ...state.byId };

    delete nextData[videoInstance.id];
    delete nextById[videoInstance.id];

    this.set({
      data: nextData,
      byId: nextById,
    });
  };

  restore() {
    const cache = this.getCache();
    const videos = Object.values(cache ?? {});
    const data: VideosData = {};
    const byId: VideosById = {};

    for (const videoInstance of videos) {
      const videoReference = videoInstance.getProps();
      data[videoReference.id] = videoInstance;
      byId[videoReference.id] = videoReference;
    }

    this.set({
      data: data,
      byId: byId,
    });
  }
}

export const videosStore = new VideosStore();
